import { useState } from "react";
import { useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import ImageCard from "./ImageCard";
import ImageModal, { type ImageData } from "./ImageModal";
import EditModal from "./EditModal";
import { useStableUrls } from "../hooks/useStableUrl";
import { useI18n } from "../i18n";

export default function ImageGrid() {
  const { t } = useI18n();
  const rawImages = useQuery(api.images.list);
  // Stabilize URLs to prevent constant image reloading
  const images = useStableUrls(rawImages);
  const [selectedImage, setSelectedImage] = useState<ImageData | null>(null);
  const [editingImage, setEditingImage] = useState<ImageData | null>(null);

  if (images === undefined) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="relative">
          <div className="w-12 h-12 border-4 border-sage-400/30 rounded-full" />
          <div className="absolute top-0 left-0 w-12 h-12 border-4 border-sage-500 rounded-full border-t-transparent animate-spin" />
        </div>
      </div>
    );
  }

  if (images.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <svg className="w-16 h-16 text-linen-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <h2 className="text-xl font-serif text-charcoal mb-2">{t.grid.noImagesTitle}</h2>
        <p className="text-charcoal-muted">
          {t.grid.noImagesHint}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-6">
        {images.map((image) => (
          <ImageCard
            key={image._id}
            image={image}
            onClick={() => image.status === "completed" && setSelectedImage(image)}
          />
        ))}
      </div>

      <ImageModal
        isOpen={selectedImage !== null}
        onClose={() => setSelectedImage(null)}
        image={selectedImage}
        onOpenEditModal={(image) => {
          setSelectedImage(null);
          setEditingImage(image);
        }}
      />

      <EditModal
        isOpen={editingImage !== null}
        onClose={() => setEditingImage(null)}
        sourceImage={editingImage}
      />
    </>
  );
}
